import * as React from 'react';
import {connect} from 'react-redux';

import set from 'lodash/set';
import unset from 'lodash/unset';

import {Routing} from 'nutshell-core/routing';
import * as Schema from 'nutshell-core/schema';
import {Session} from 'nutshell-core/session';
import type {User, FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {FloatingLabelTextField} from 'shells/floating-label-textfield/floating-label-textfield';
import {ErrorMessage} from 'shells/form/error-message';
import {FooterBar} from 'shells/modal/footer-bar';

// @ts-expect-error
import * as actions from '../../../dashboard-actions';
// @ts-expect-error
import * as dashboardSelectors from '../../../dashboard-selectors';

import type {NutshellState} from '../../../../../store';

// @ts-expect-error
import {DashConfigPreview} from '../dash-config-preview';

import {FunnelForm} from './forms/funnel-form';
import {LeaderboardForm} from './forms/leaderboard-form';
import {ListForm} from './forms/list-form';
import {ListsummariesForm} from './forms/listsummaries-form';
import {MetricsForm} from './forms/metrics-form';
import {QuotasForm} from './forms/quotas-form';

import styles from './dash-config-form.module.css';

const mapStateToProps = function (state: NutshellState): StateProps {
    return {
        schema: dashboardSelectors.getDashboardSchema(state),
        isAddingNewDash: dashboardSelectors.getIsAddingNewDash(state),
        loggedInUser: Session.getSessionStateUser(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = (dispatch, ownProps: OwnProps) => {
    return {
        // @ts-expect-error
        onFormSubmit: (formValues) => {
            const data = {
                dash: ownProps.dash,
                formValues: formValues,
            } as const;

            return actions.submitDashboardConfigForm(data, dispatch);
        },
        // @ts-expect-error
        updateDashboardPreview: (formValues) => {
            dispatch(actions.fetchDashboardPreview(formValues));
        },
        // @ts-expect-error
        updateDashboardPreviewName: (name) => {
            dispatch(actions.updateDashboardPreviewName(name));
        },
    };
};

type OwnProps = {
    initialValues: {
        name: string;
        value: string;
    };
    dash: any;
    isLoading: boolean;
    isRefreshing: boolean;
    errorMessage?: string;
    dashboardPreview: any;
    onPopoverOpen: () => any;
    onPopoverClose: () => any;
    onModalClose: () => any;
};

type StateProps = {
    schema: Schema.Properties;
    isAddingNewDash: boolean;
    loggedInUser: User | null | undefined;
};

type DispatchProps = {
    onFormSubmit: (formValues: any) => any;
    updateDashboardPreview: (newFormValues: any) => any;
    updateDashboardPreviewName: (newName: string) => any;
};

type State = {
    showWarning: boolean;
    warningMsg: string | React.ReactElement<any>;
    name: string;
    value: string;
    errorMessage: string | null | undefined;
    isSubmitting: boolean;
};

type Props = OwnProps & StateProps & DispatchProps;

/*
 * Although this is using redux-form, we are using in a non-standard and somewhat hacky way.
 * Instead of passing in the actual form fields, we are only passing in `name` and `value` keys.
 *
 * Then, within the componenet and the sub-form component, we deserialize the value into a set of
 * fields and render those.  When they change, we then paramaterize them back into a string and
 * call redux-form's `onChange` handler for `value` with the new string value.
 *
 * A cleaner way might be to define the actual form fields and their default values up-front,
 * and only serialize them when we are submitting the form.  Or better yet, don't serialize it
 * until we need to make the ajax call to the backend, and do the serialization there while
 * storing the dash configuration as an object.
 */
export class DashConfigFormComponent extends React.Component<Props, State> {
    errorMsgTimeout: any;

    constructor(props: Props) {
        super(props);
        this.state = {
            showWarning: false,
            warningMsg: '',
            isSubmitting: false,
            errorMessage: null,
            name: props.initialValues.name,
            value: props.initialValues.value,
        };
    }

    componentWillUnmount() {
        clearTimeout(this.errorMsgTimeout);
    }

    render() {
        const formComponents = {
            leaderboard: LeaderboardForm,
            listSummaries: ListsummariesForm,
            list: ListForm,
            quotas: QuotasForm,
            metrics: MetricsForm,
            funnel: FunnelForm,
        } as const;

        // @ts-expect-error
        const FormComponent = formComponents[this.props.dash.type];
        const nameIsInvalid = !this.state.name.length;

        return (
            <React.Fragment>
                <div className={styles['scroll-container']}>
                    <div className={styles['name-container']}>
                        <FloatingLabelTextField
                            label={nameIsInvalid ? 'Name of card is required' : 'Name of card'}
                            name='cardName'
                            autoFocus={true}
                            required={true}
                            isValid={!nameIsInvalid}
                            debounce={200}
                            value={this.state.name}
                            onChange={this.handleFormNameChange}
                            onFocus={(e) => e.target.select()}
                        />
                    </div>
                    <div className={styles['form-and-preview-container']}>
                        <div className={styles['form-container']}>
                            <FormComponent
                                value={this.state.value}
                                dash={this.props.dash}
                                schema={this.props.schema}
                                submitFailed={this.state.errorMessage}
                                updateValue={this.updateValue}
                                onFilterSubmit={this.handleFilterSubmit}
                                onValueObjectSubmit={this.handleValueObjectSubmit}
                                onFilterRemove={this.handleFormFilterRemove}
                                onFormFieldChange={this.handleFormFilterChange}
                                onFormNameChange={this.handleFormNameChange}
                                onCustomError={this.handleCustomError}
                                onCustomErrorCleared={this.handleCustomErrorCleared}
                                onPopoverOpen={this.props.onPopoverOpen}
                                onPopoverClose={this.props.onPopoverClose}
                            />
                            <div className={styles['error-container']}>
                                <div
                                    className={
                                        this.state.showWarning
                                            ? styles['warning-shown']
                                            : styles['warning-hidden']
                                    }
                                >
                                    <ErrorMessage>{this.state.warningMsg}</ErrorMessage>
                                </div>
                            </div>
                        </div>
                        <div className={styles['preview-container']}>
                            <h3 className={styles['preview-label']}>Preview</h3>
                            <DashConfigPreview
                                type={this.props.dash.type}
                                title={this.state.name}
                                isLoading={this.props.isLoading}
                                isRefreshing={this.props.isRefreshing}
                                errorMessage={this.props.errorMessage}
                                preview={this.props.dashboardPreview}
                                loggedInUser={this.props.loggedInUser}
                                query={this.state.value}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles['submit-button']}>
                    <FooterBar
                        left={
                            <ButtonMover size='big' direction='left'>
                                <Button variant='text' size='big' onClick={this.props.onModalClose}>
                                    Cancel
                                </Button>
                            </ButtonMover>
                        }
                        right={
                            <SaveButton
                                // @ts-expect-error
                                onClick={this.handleSubmitForm}
                                buttonText={
                                    this.props.isAddingNewDash ? 'Save card to dashboard' : 'Save'
                                }
                                disabled={nameIsInvalid || !this.state.value.length}
                                isSaving={this.state.isSubmitting}
                            />
                        }
                    />
                </div>
            </React.Fragment>
        );
    }

    handleSubmitForm = () => {
        this.setState({
            isSubmitting: true,
            errorMessage: null,
        });

        this.props
            .onFormSubmit({
                name: this.state.name,
                value: this.state.value,
            })
            .then(
                () => {
                    this.setState({isSubmitting: false});
                },
                () => {
                    this.setState({
                        isSubmitting: false,
                        errorMessage: 'shoot',
                    });
                }
            );
    };

    updateValue = (newValue: string) => {
        this.setState({value: newValue});

        this.props.updateDashboardPreview({
            name: this.state.name,
            value: newValue,
        });
    };

    handleFormNameChange = (newName: string) => {
        this.setState({name: newName});

        this.props.updateDashboardPreviewName(newName);
    };

    handleFilterSubmit = (data: FilterObject, key: string) => {
        const filterName = Object.keys(data)[0];
        // @ts-expect-error
        this.handleFormFilterChange(key, data[filterName]);
    };

    /**
     * This takes a complete filter object, params it, and updates the form 'value' field.
     *
     * It is useful when we need to apply a batch of filter parameters at one time,
     * e.g. for quotas which require a tuple of values.
     *
     * @param  {Object} filters - A filter object
     * @return {void}
     */
    handleValueObjectSubmit = (filters: FilterObject) => {
        const paramedNewValue = Routing.param(filters);
        this.updateValue(paramedNewValue);
    };

    handleFormFilterChange = (key: string, newValue: string) => {
        const deparamedCurrentValue = Routing.deparam(this.state.value);
        set(deparamedCurrentValue, key, newValue);
        const paramedNewValue = Routing.param(deparamedCurrentValue);

        this.updateValue(paramedNewValue);
    };

    handleFormFilterRemove = (key: string) => {
        const deparamedCurrentValue = Routing.deparam(this.state.value);
        unset(deparamedCurrentValue, key);
        const paramedNewValue = Routing.param(deparamedCurrentValue);

        this.updateValue(paramedNewValue);
    };

    handleCustomError = (errorMessage: string | React.ReactElement<any>) => {
        clearTimeout(this.errorMsgTimeout);
        this.setState({
            showWarning: true,
            warningMsg: errorMessage,
        });
        this.errorMsgTimeout = setTimeout(() => {
            this.setState({showWarning: false});
        }, 8000);
    };

    handleCustomErrorCleared = () => {
        this.setState({showWarning: false});
        clearTimeout(this.errorMsgTimeout);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const DashConfigForm = connector(DashConfigFormComponent);
